import type { OperationVariables, TypedDocumentNode } from '@apollo/client';
import { skipToken, useQuery } from '@apollo/client/react';
import { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';

import type {
  Filter,
  Table,
  TableFilterDefinition,
  TableSelection,
  TableSortOrder,
  TableSortOrderDefinition,
} from '../components/AutoCollection/utils';
import type {
  FilterDefinition,
  FilterInput,
  PageInput,
  SortOrder,
  SortOrderInput,
} from '../gql/graphql';
import {
  getSortOrderType,
  TableFilterVariant,
  TableSortOrderType,
} from '../components/AutoCollection/utils';
import { now } from '../topics/time';

const SORT_ORDER_ASCENDING = 'ASCENDING' as SortOrder;
const SORT_ORDER_DESCENDING = 'DESCENDING' as SortOrder;

type QueryOptions<TData, TVariables extends OperationVariables> = Omit<
  useQuery.Options<TData, TVariables>,
  'variables'
>;

type QueryOptionsWithVariables<TData, TVariables extends OperationVariables> = useQuery.Options<
  TData,
  TVariables
>;

type StateTuple<T> = readonly [T, (value: T) => void];

interface ServerPage<D> {
  items: D[];
  totalCount: number;
  currentPage?: number;
  totalPages?: number;
}

function toClientVariant(variant: string): TableFilterVariant {
  switch (variant) {
    case 'TEXT':
      return TableFilterVariant.Text;
    case 'NUMBER':
      return TableFilterVariant.Number;
    case 'SELECT':
      return TableFilterVariant.Select;
    case 'NUMBER_RANGE':
      return TableFilterVariant.NumberRange;
    case 'DATE_RANGE':
      return TableFilterVariant.DateRange;
    case 'MULTI_SELECT':
      return TableFilterVariant.MultiSelect;
    case 'BOOL':
      return TableFilterVariant.Bool;
    default:
      return TableFilterVariant.Text;
  }
}

function getOrder(order: TableSortOrderType): SortOrder {
  switch (order) {
    case TableSortOrderType.Ascending:
      return SORT_ORDER_ASCENDING;
    case TableSortOrderType.Descending:
      return SORT_ORDER_DESCENDING;
    default:
      return SORT_ORDER_DESCENDING;
  }
}

const DEFAULT_SORT_ORDER: SortOrderInput = {
  fieldName: '',
  order: SORT_ORDER_DESCENDING,
};

export function offsetFromPageInput(pageInput: PageInput): number {
  return ((pageInput.page ?? 1) - 1) * pageInput.limit;
}

export interface UseTableProps<
  RowType,
  DataQuery,
  DataQueryVariables extends OperationVariables,
  FilterDefinitionsQuery,
  FilterDefinitionsQueryVariables extends OperationVariables,
  SortOrderDefinitionsQuery,
  SortOrderDefinitionsQueryVariables extends OperationVariables,
> {
  data: {
    query: TypedDocumentNode<DataQuery, DataQueryVariables>;
    select: (result: DataQuery) => ServerPage<RowType>;
    variables: (
      pageInput: PageInput,
      filters: FilterInput[],
      sortOrder: SortOrderInput,
    ) => DataQueryVariables;
    options?: QueryOptions<DataQuery, DataQueryVariables>;
  };
  filterDefinitions?: {
    query: TypedDocumentNode<FilterDefinitionsQuery, FilterDefinitionsQueryVariables>;
    select: (result: FilterDefinitionsQuery) => FilterDefinition[];
    variables?: () => FilterDefinitionsQueryVariables;
    options?: QueryOptions<FilterDefinitionsQuery, FilterDefinitionsQueryVariables>;
  };
  sortOrderDefinitions?: {
    query: TypedDocumentNode<SortOrderDefinitionsQuery, SortOrderDefinitionsQueryVariables>;
    select: (result: SortOrderDefinitionsQuery | null) => TableSortOrderDefinition[] | null;
    variables: () => SortOrderDefinitionsQueryVariables;
    options?: QueryOptions<SortOrderDefinitionsQuery, SortOrderDefinitionsQueryVariables>;
  };
  defaultSortOrder?: SortOrderInput;
  defaultLimit?: number;
  overrides?: {
    pageNumber?: StateTuple<number>;
    limit?: StateTuple<number>;
  };
}

export function useTable<
  RowType,
  DataQuery,
  DataQueryVariables extends OperationVariables,
  FilterDefinitionsQuery,
  FilterDefinitionsQueryVariables extends OperationVariables,
  SortOrderDefinitionsQuery,
  SortOrderDefinitionsQueryVariables extends OperationVariables,
>({
  data,
  filterDefinitions,
  sortOrderDefinitions,
  defaultSortOrder = DEFAULT_SORT_ORDER,
  defaultLimit = 25,
  overrides,
}: UseTableProps<
  RowType,
  DataQuery,
  DataQueryVariables,
  FilterDefinitionsQuery,
  FilterDefinitionsQueryVariables,
  SortOrderDefinitionsQuery,
  SortOrderDefinitionsQueryVariables
>): Table<RowType> {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set());
  const selectRow = useCallback((id: string) => {
    setSelectedIds((prev) => {
      if (prev.has(id)) return prev;
      const next = new Set(prev);
      next.add(id);
      return next;
    });
  }, []);
  const selectManyRows = useCallback((newIds: string[]) => {
    setSelectedIds((prev) => {
      let changed = false;
      const next = new Set(prev);
      for (const id of newIds) {
        if (!next.has(id)) {
          next.add(id);
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, []);
  const deselectRow = useCallback((id: string) => {
    setSelectedIds((prev) => {
      if (!prev.has(id)) return prev;
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  }, []);
  const toggleRow = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);
  const clearSelection = useCallback(() => {
    setSelectedIds((prev) => (prev.size === 0 ? prev : new Set()));
  }, []);
  const isRowSelected = useCallback((id: string) => selectedIds.has(id), [selectedIds]);
  const selection = useMemo<TableSelection>(
    () => ({
      ids: selectedIds,
      count: selectedIds.size,
      isSelected: isRowSelected,
      toggle: toggleRow,
      select: selectRow,
      selectMany: selectManyRows,
      deselect: deselectRow,
      clear: clearSelection,
    }),
    [selectedIds, isRowSelected, toggleRow, selectRow, selectManyRows, deselectRow, clearSelection],
  );

  const localLimit = useState<number>(defaultLimit);
  const localPageNumber = useState<number>(1);
  const [limit, setLimitState] = overrides?.limit ?? localLimit;
  const [currentPage, setCurrentPageState] = overrides?.pageNumber ?? localPageNumber;
  const [asOf, setAsOf] = useState(now());
  const [filters, setFiltersState] = useState<Filter[]>([]);
  const [sortOrder, setSortOrder] = useState<TableSortOrder>({
    column: defaultSortOrder.fieldName,
    orderBy: getSortOrderType(defaultSortOrder.order),
  });
  const filterInputs: FilterInput[] = useMemo(
    () =>
      filters
        .filter((filter) => {
          switch (filter.type) {
            case TableFilterVariant.MultiSelect:
              return filter.values.length > 0;
            case TableFilterVariant.Select:
              return filter.value.length > 0;
            default:
              return true;
          }
        })
        .map((filter) => {
          switch (filter.type) {
            case TableFilterVariant.MultiSelect:
              return {
                multiSelect: {
                  key: filter.key,
                  values: filter.values,
                },
              } as FilterInput;
            case TableFilterVariant.Bool:
              return {
                bool: {
                  key: filter.key,
                  value: filter.value,
                },
              } as FilterInput;
            case TableFilterVariant.Number:
              return {
                number: {
                  key: filter.key,
                  value: filter.value,
                },
              } as FilterInput;
            case TableFilterVariant.Text:
              return {
                text: {
                  key: filter.key,
                  value: filter.value,
                },
              } as FilterInput;
            case TableFilterVariant.Select:
              return {
                select: {
                  key: filter.key,
                  value: filter.value,
                },
              } as FilterInput;
            case TableFilterVariant.DateRange:
              return {
                dateRange: {
                  key: filter.key,
                  start: filter.start,
                  end: filter.end,
                },
              } as FilterInput;
            case TableFilterVariant.NumberRange:
              return {
                numberRange: {
                  key: filter.key,
                  start: filter.start,
                  end: filter.end,
                },
              } as FilterInput;
            default:
              return undefined;
          }
        })
        .filter((filter) => !!filter),
    [filters],
  );
  const sortInput = useMemo<SortOrderInput>(
    () => ({
      fieldName: sortOrder.column,
      order: getOrder(sortOrder.orderBy),
    }),
    [sortOrder],
  );
  const dataResult = useQuery<DataQuery, DataQueryVariables>(data.query, {
    ...data.options,
    variables: data.variables(
      {
        page: currentPage,
        asOf: asOf.toISO(),
        limit,
      },
      filterInputs,
      sortInput,
    ),
  } as QueryOptionsWithVariables<DataQuery, DataQueryVariables>);
  const dataForRender = dataResult.data ?? dataResult.previousData;

  const filterDefinitionsResult = useQuery<FilterDefinitionsQuery, FilterDefinitionsQueryVariables>(
    filterDefinitions?.query ??
      (data.query as unknown as TypedDocumentNode<
        FilterDefinitionsQuery,
        FilterDefinitionsQueryVariables
      >),
    filterDefinitions !== undefined
      ? ({
          ...filterDefinitions.options,
          variables: filterDefinitions.variables ? filterDefinitions.variables() : {},
        } as QueryOptionsWithVariables<FilterDefinitionsQuery, FilterDefinitionsQueryVariables>)
      : skipToken,
  );
  const filterSelector = useRef(filterDefinitions?.select);
  filterSelector.current = filterDefinitions?.select;

  const tableFilterDefinitions: TableFilterDefinition[] | undefined = useMemo(() => {
    if (!filterDefinitions || filterDefinitionsResult.data === undefined) {
      return undefined;
    }
    const selector = filterSelector.current;
    if (!selector) return undefined;
    return selector(filterDefinitionsResult.data).map((definition) => ({
      id: definition.id,
      label: definition.label,
      variant: toClientVariant(definition.variant),
      options: definition.options.map((option) => ({
        label: option.label,
        disabled: option.disabled,
        value: option.value,
      })),
    }));
  }, [filterDefinitions, filterDefinitionsResult.data]);
  const filterDefinitionsByID = useMemo(() => {
    const map: Record<string, TableFilterDefinition> = {};
    if (!tableFilterDefinitions) {
      return map;
    }
    for (const definition of tableFilterDefinitions) {
      map[definition.id] = definition;
    }
    return map;
  }, [tableFilterDefinitions]);
  const filtersByKey = useMemo(() => {
    const map: Record<string, Filter> = {};
    for (const filter of filters) {
      map[filter.key] = filter;
    }
    return map;
  }, [filters]);

  const sortOrderDefinitionsResults = useQuery<
    SortOrderDefinitionsQuery,
    SortOrderDefinitionsQueryVariables
  >(
    sortOrderDefinitions?.query ??
      (data.query as unknown as TypedDocumentNode<
        SortOrderDefinitionsQuery,
        SortOrderDefinitionsQueryVariables
      >),
    sortOrderDefinitions !== undefined && !sortOrderDefinitions.options?.skip
      ? ({
          ...sortOrderDefinitions.options,
          variables: sortOrderDefinitions.variables(),
        } as QueryOptionsWithVariables<
          SortOrderDefinitionsQuery,
          SortOrderDefinitionsQueryVariables
        >)
      : skipToken,
  );
  const sortOrderSelector = useRef(sortOrderDefinitions?.select);
  sortOrderSelector.current = sortOrderDefinitions?.select;
  const orderDefinitions: TableSortOrderDefinition[] | undefined = useMemo(() => {
    if (sortOrderDefinitionsResults.data === undefined) {
      return undefined;
    }
    return sortOrderSelector.current?.(sortOrderDefinitionsResults.data)?.map((definition) => ({
      name: definition.name,
      value: definition.value,
    }));
  }, [sortOrderDefinitionsResults.data]);

  const serverPage = dataForRender ? data.select(dataForRender) : undefined;
  const totalPages = serverPage
    ? serverPage.totalPages ?? Math.max(1, Math.ceil(serverPage.totalCount / limit))
    : 1;
  const isLoading = dataResult.loading && !dataForRender;

  const goTo = useCallback(
    (p: number) => setCurrentPageState(Math.max(1, Math.min(p, totalPages))),
    [totalPages, setCurrentPageState],
  );

  const setLimit = useCallback(
    (l: number) => {
      setLimitState(l);
      setCurrentPageState(1);
    },
    [setLimitState, setCurrentPageState],
  );

  return {
    filters,
    selection,
    setFilters(newFilters) {
      setFiltersState(newFilters);
      setCurrentPageState(1);
    },
    sortDefinitions: orderDefinitions,
    filterDefinitions: tableFilterDefinitions,
    isLoading,
    error: dataResult.error ?? null,
    page: serverPage
      ? {
          items: serverPage.items,
          page: serverPage.currentPage ?? currentPage,
          totalPages,
          totalCount: serverPage.totalCount,
        }
      : {
          items: [],
          page: currentPage,
          totalPages: 1,
          totalCount: 0,
        },
    limit,
    setLimit,
    setSortOrderType(order: TableSortOrderType) {
      setSortOrder((prev) => ({
        ...prev,
        orderBy: order,
      }));
    },
    getSortOrder(): TableSortOrder {
      return sortOrder;
    },
    setSortColumn(fieldName: string) {
      setSortOrder((prev) => ({
        ...prev,
        column: fieldName,
      }));
    },
    getFilterDefinition(filterID) {
      return filterDefinitionsByID[filterID];
    },
    getFilterByID(filterID) {
      return filtersByKey[filterID];
    },
    updateFilter(updatedFilter) {
      setFiltersState((prev) =>
        prev.map((filter) => {
          if (filter.key === updatedFilter.key) {
            return updatedFilter;
          }
          return filter;
        }),
      );
      setCurrentPageState(1);
    },
    refresh() {
      setAsOf(now());
    },
    clearFilter(filterID) {
      setFiltersState((prev) => prev.filter((filter) => filter.key !== filterID));
      setCurrentPageState(1);
    },
    goTo,
  };
}

export const TableContext = createContext<Table<unknown> | null>(null);

export function useTableContext<T = unknown>(): Table<T> {
  const context = useContext(TableContext);
  if (context === null) {
    throw new Error('useTable must be used within a TableContext.Provider');
  }
  return context as Table<T>;
}
